import { useCallback, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { useHotkey } from '@shared/hooks/use-hotkey';
import { usePersistedToggle } from '@shared/hooks/use-persisted-toggle';
import { CrumbContext } from '@shared/ui/crumb-context';
import { CommandPalette, type CommandItem } from '@shared/ui/command-palette';
import { NAVIGATION } from './navigation';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

const SIDEBAR_KEY = 'dogitec.sidebar.collapsed';

/**
 * Armazon de la aplicacion: barra lateral, cabecera y area de trabajo.
 *
 * El area de trabajo desplaza por si misma en lugar de desplazar la pagina
 * entera: en una tabla de mil filas, conservar a la vista la ruta y los
 * filtros evita perder de vista que almacen se esta consultando.
 */
export function AppShell() {
  const [collapsed, toggleSidebar] = usePersistedToggle(SIDEBAR_KEY);
  const [crumbLabel, setCrumbLabel] = useState<string | null>(null);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const navigate = useNavigate();

  const openCommands = useCallback(() => {
    setCommandsOpen(true);
  }, []);
  const closeCommands = useCallback(() => {
    setCommandsOpen(false);
  }, []);

  useHotkey('k', openCommands, { ctrlOrMeta: true });
  useHotkey('b', toggleSidebar, { ctrlOrMeta: true });

  const commands = useMemo<readonly CommandItem[]>(
    () =>
      NAVIGATION.flatMap((group) =>
        group.items.map((item) => ({
          id: item.to,
          label: item.label,
          group: group.label,
          ...(item.pending === true ? { hint: 'proximamente', disabled: true } : {}),
          run: () => {
            void navigate(item.to);
          },
        })),
      ),
    [navigate],
  );

  // El valor se memoriza porque cambia en cada render si no: el contexto
  // haria re-renderizar el armazon entero al teclear en cualquier pantalla.
  const crumb = useMemo(() => ({ label: crumbLabel, setLabel: setCrumbLabel }), [crumbLabel]);

  return (
    <CrumbContext value={crumb}>
      <div className="flex h-dvh overflow-hidden bg-bg">
        {/* Primer elemento enfocable: permite saltar la navegacion con el
          teclado, requisito de WCAG (spec 51). */}
        <a href="#contenido" className="sr-only skip-link">
          Saltar al contenido principal
        </a>

        <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />

        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          <Topbar onOpenCommands={openCommands} />
          {/*
          El relleno del area de trabajo vive AQUI y no en cada pantalla.
          Estaba en ninguna de las dos: treinta paginas devolvian su contenido
          pegado al borde del menu. Ponerlo en un solo sitio ademas garantiza
          que todas respiren igual, que es lo que hace que se lean como un
          mismo producto y no como treinta.
        */}
          <main id="contenido" className="flex-1 px-6 py-6">
            <Outlet />
          </main>
        </div>

        {/* Se monta solo cuando esta abierta: al montar, su estado interno
          nace limpio y no hace falta sincronizarlo con un efecto. */}
        {commandsOpen && <CommandPalette onClose={closeCommands} items={commands} />}
      </div>
    </CrumbContext>
  );
}
